/**
 * useAuth — фасад над `atoms/auth/`. Два контура в одном хуке.
 *
 *   const {team, client, logout} = useAuth();
 *   team.user / team.isAuthed / team.setSession(token, user)
 *   client.user / client.isAuthed / client.setSession(token, user)
 *
 * Один импорт вместо ковыряния по атомам в каждом компоненте.
 */

import {useAtomValue, useSetAtom} from 'jotai';

import type {ClientUser, TeamUser} from '@services/auth';
import {
  clientTokenAtom,
  clientUserAtom,
  isClientAuthedAtom,
  isTeamAuthedAtom,
  logoutAtom,
  teamTokenAtom,
  teamUserAtom,
} from '@atoms/auth';

export type UseAuthResult = {
  team: {
    user: TeamUser | null;
    token: string | null;
    isAuthed: boolean;
    setSession: (token: string, user: TeamUser) => void;
  };
  client: {
    user: ClientUser | null;
    token: string | null;
    isAuthed: boolean;
    setSession: (token: string, user: ClientUser) => void;
  };
  logout: () => void;
};

export const useAuth = (): UseAuthResult => {
  const teamUser = useAtomValue(teamUserAtom);
  const teamToken = useAtomValue(teamTokenAtom);
  const teamAuthed = useAtomValue(isTeamAuthedAtom);
  const clientUser = useAtomValue(clientUserAtom);
  const clientToken = useAtomValue(clientTokenAtom);
  const clientAuthed = useAtomValue(isClientAuthedAtom);

  const setTeamToken = useSetAtom(teamTokenAtom);
  const setTeamUser = useSetAtom(teamUserAtom);
  const setClientToken = useSetAtom(clientTokenAtom);
  const setClientUser = useSetAtom(clientUserAtom);
  const triggerLogout = useSetAtom(logoutAtom);

  return {
    team: {
      user: teamUser,
      token: teamToken,
      isAuthed: teamAuthed,
      setSession: (token, user) => {
        setTeamToken(token);
        setTeamUser(user);
      },
    },
    client: {
      user: clientUser,
      token: clientToken,
      isAuthed: clientAuthed,
      setSession: (token, user) => {
        setClientToken(token);
        setClientUser(user);
      },
    },
    logout: () => triggerLogout(),
  };
};
